import React, { useEffect, useState } from 'react';
import { SignupLayout } from './ui/SignupLayout';
import { Spinner } from 'reactstrap';
import { useFormContext } from '../context/FormContext';
import Session from './auth/Session';

// Maps currentStep string to its route
const STEP_ROUTES = {
    eligibility:     '/signup/eligibility',
    dataPoints:      '/signup/data-points',
    confirmDetails:  '/signup/confirm-details',
    additionalParty: '/signup/additional-party',
    finalize:        '/signup/finalize',
    sign:            '/signup/sign',
    payment:         '/signup/payment',
    paymentBank:     '/signup/payment/bank',
    paymentCard:     '/signup/payment/card',
    paymentReview:   '/signup/payment/review',
    complete:        '/dashboard',
};

export function ContinueQuote({ history }) {
    const { setFields, resetForm } = useFormContext();
    const [status, setStatus] = useState('validating'); // validating | error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        validateOtc();
    }, []);

    const validateOtc = async () => {
        // Parse email and otc from URL query params
        const params = new URLSearchParams(window.location.search);
        const email = params.get('email');
        const otc = params.get('otc');

        if (!email || !otc) {
            setErrorMessage('This link is invalid. Please request a new one.');
            setStatus('error');
            return;
        }

        // If the user is already logged in with a valid session, skip OTC validation
        // and just redirect them to the correct step using their current session
        const existingToken = Session.getToken();
        const existingUserName = Session.getUserName();
        if (existingToken && existingUserName?.toLowerCase() === email.toLowerCase()) {
            try {
                // Already logged in - fetch their latest quote data and redirect
                const response = await fetch(
                    `api/Quote/GetDashboardDataByEmail?email=${encodeURIComponent(email)}`,
                    { headers: { Authorization: `Bearer ${existingToken}` } }
                );

                if (!response.ok) {
                    history.push('/signup/eligibility');
                    return;
                }

                const data = await response.json();
                const quote = data?.data?.quote;

                if (!quote) {
                    history.push('/signup/eligibility');
                    return;
                }

                const quoteAgeInDays = quote.createdOn
                    ? (Date.now() - new Date(quote.createdOn).getTime()) / (1000 * 60 * 60 * 24)
                    : 0;

                hydrateFormAndRedirect({
                    quoteData: quote,
                    quoteExpired: quoteAgeInDays >= 7,
                    currentStep: quoteAgeInDays >= 7 ? 'eligibility' : quote.currentStep,
                });
            } catch (err) {
                console.error('Error loading existing session quote:', err);
                history.push('/signup/eligibility');
            }
            return;
        }

        try {
            const response = await fetch('api/ContinueQuote/ValidateOtc', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Email: email, Otc: otc }),
            });

            const result = await response.json();

            if (!result.success) {
                setErrorMessage(result.errorMessage || 'This link is invalid or has expired.');
                setStatus('error');
                return;
            }

            Session.setUserSession(
                result.userName,
                result.token,
                result.userId,
                result.userRoles
            );

            // Hydrate FormContext and redirect
            hydrateFormAndRedirect(result);

        } catch (err) {
            console.error('ValidateOtc error:', err);
            setErrorMessage('Something went wrong. Please try again or request a new link.');
            setStatus('error');
        }
    };

    const hydrateFormAndRedirect = (result) => {
        const q = result.quoteData;
        if (!q) {
            // No quote data - just send to eligibility
            history.push('/signup/eligibility');
            return;
        }

        // Reset form first to clear any stale data
        resetForm();

        // Hydrate FormContext with all quote data from the DB
        setFields({
                QuoteId: q.id,
                currentStep: q.currentStep,
 
                // Property
                TargetAddressOne: q.targetAddressOne || '',
                TargetAddressTwo: q.targetAddressTwo || '',
                TargetCity: q.targetCity || '',
                TargetState: q.targetState || '',
                TargetZipCode: q.targetZipCode || '',
                TargetLat: q.targetLat || null,
                TargetLng: q.targetLng || null,
                TargetAddressFormatted: q.targetAddressFormatted || '',
                TargetHomeStatus: q.targetHomeStatus || '',
                ExposedHome: q.exposedHome || false,
                ExposedLandImprovements: q.exposedLandImprovements || false,
                ExposedHardscaping: q.exposedHardscaping || false,
                ExposedContents: q.exposedContents || false,
                ExposedValuables: q.exposedValuables || false,
                ExposedVehicles: q.exposedVehicles || false,
                ExposedLivingExpenses: q.exposedLivingExpenses || false,
                IsExposedValueOverMillion: q.isExposedValueOverMillion,
 
                // Contact
                FirstName: q.firstName || '',
                LastName: q.lastName || '',
                UserName: q.email || '',
                PhoneNumberPrimary: q.phoneNumberPrimary || '',
                PhoneNumberSecondary: q.phoneNumberSecondary || '',
                IsReceivingCriticalMessages: q.isReceivingCriticalMessages || false,
                OwnerType: q.ownerType || 'INDIVIDUAL',
 
                // Coverage
                CoverageAmount: q.coverageAmount || 0,
                EffectiveDate: q.effectiveDate || '',
 
                // Quote
                QuoteNumber: q.quoteNumber || '',
                SelectedTriggerType: q.selectedTriggerType || '',
                SelectedTotalPremium: q.selectedTotalPremium || 0,
 
                // Mailing
                ContactAddressOne: q.contactAddressOne || '',
                ContactAddressTwo: q.contactAddressTwo || '',
                ContactCity: q.contactCity || '',
                ContactState: q.contactState || '',
                ContactZipCode: q.contactZipCode || '',
                ContactCountry: q.contactCountry || 'USA',
                MailingSameAsProperty: q.mailingSameAsProperty || false,
 
                // Additional party - name comes back as a single string from DB
                // split on first space to approximate first/last for display
                HasAdditionalParties: q.hasAdditionalParties || false,
                AdditionalPartyType: q.additionalPartyType || '',
                AdditionalPartyFirstName: q.additionalPartyName?.split(' ')[0] || '',
                AdditionalPartyLastName: q.additionalPartyName?.split(' ').slice(1).join(' ') || '',
                AdditionalPartyAddressOne: q.additionalPartyAddressOne || '',
                AdditionalPartyAddressTwo: q.additionalPartyAddressTwo || '',
                AdditionalPartyCity: q.additionalPartyCity || '',
                AdditionalPartyState: q.additionalPartyState || '',
                AdditionalPartyZipCode: q.additionalPartyZipCode || '',
                AdditionalPartyCountry: q.additionalPartyCountry || 'USA',
 
                // Policy
                PolicyNumber: q.policyNumber || '',
                ExpiryDate: q.policyExpiryDate || '',
                PolicyTotalPremium: q.policyTotalPremium || null,
        });

        // If quote expired -> send to eligibility so NormanMax creates a fresh quote
        // If quote valid -> send to the step they were on
        if (result.quoteExpired) {
            history.push('/signup/eligibility');
        } else {
            const route = STEP_ROUTES[result.currentStep] || '/signup/eligibility';
            history.push(route);
        }
    };

    return (
        <SignupLayout showProgress={false}>
            {status === 'validating' && (
                <>
                    <div className="dp-loading" aria-label="Verifying your link…">
                        <Spinner />
                    </div>
                    <p className="signup-section__desc" style={{ textAlign: 'center' }}>
                        Verifying your link, please wait…
                    </p>
                </>
            )}

            {status === 'error' && (
                <div className="dp-error">
                    <p className="dp-error__message">{errorMessage}</p>
                    <p className="dp-error__help">
                        Need a new link?{' '}
                        <button
                            className="finalize__link-btn"
                            onClick={() => history.push('/find-quote')}
                        >
                            Request one here
                        </button>
                    </p>
                </div>
            )}
        </SignupLayout>
    );
}